import {
  getCurrentEntityPendingChanges,
  getFormattedFullAddress,
} from 'app/pages/loanApplication/LoanApplication.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  ADDRESS_FIELDS_TO_COUNT,
  AddressField,
  DEFAULT_COUNTRY,
  HousingType,
} from 'constants/loanApplication';
import {
  HistoryPendingChangesActionType,
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import {
  checkBooleanPendingChangeVisibility,
  formatDatePendingChange,
  mergePendingChanges,
} from 'utils/pendingChanges';

import {
  previousAddressModifiedFieldsToExclude,
  PreviousAddressTableColumnId,
} from './Address.constants';

export const createNewPreviousAddressData = () => {
  return {
    id: _.uniqueId(),
    // * This flag is needed for cases when we add new entity but not save it
    isNewlyAdded: true,
    isAdded: true,
    country: DEFAULT_COUNTRY,
    isOutsideUsa: false,
  };
};

export const orderPreviousAddresses = ({ previousAddresses, order }) =>
  _.orderBy(
    previousAddresses,
    [
      previousAddress => {
        const value = _.get(previousAddress, order.id);

        switch (order.id) {
          case PreviousAddressTableColumnId.MONTHLY_RENT:
            return Number(value);

          default:
            return _.toLower(value);
        }
      },
    ],
    [order.direction],
  );

export const preparePreviousAddressesModifiedFields = ({
  addresses,
  previousAddressesPendingChangesData,
  initialPreviousAddressesValuesMap,
}) =>
  addresses.map(addressValues => {
    const pendingChanges = previousAddressesPendingChangesData[addressValues.id];
    const addressPendingChanges = previousAddressesPendingChangesData[addressValues.addressId];
    const currentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialPreviousAddressesValuesMap[addressValues.id],
      currentValues: addressValues,
    });

    if (!pendingChanges && !addressPendingChanges && _.isEmpty(currentPendingChanges)) {
      return {
        ...addressValues,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const initialPendingChanges = {
      ...pendingChanges,
      ...addressPendingChanges,
    };
    const mergedPendingChanges = filterPreviousAddressForm(
      mergePendingChanges(initialPendingChanges, currentPendingChanges),
      true,
    );

    const modifiedFields = Object.keys(mergedPendingChanges);

    const isFullAddressChanged = !!_.intersection(modifiedFields, ADDRESS_FIELDS_TO_COUNT).length;

    const normalizedModifiedFields = modifiedFields.reduce(
      (acc, field) => {
        const pendingChange = mergedPendingChanges[field];

        if (pendingChange.newValue === pendingChange.initialValue) {
          return acc;
        }

        if (previousAddressModifiedFieldsToExclude.includes(field)) {
          return acc;
        }

        acc.push(_.upperFirst(field));

        return acc;
      },
      getConditionalArray(isFullAddressChanged, ['FullAddress']),
    );

    const historyActionType = mergedPendingChanges[modifiedFields[0]]?.actionType;
    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...addressValues,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });

export const getPreviousAddressValuesToSave = ({ initialData, updatedData }) => {
  const formattedUpdatedData = {
    ...initialData,
    ...updatedData,
    housingType: updatedData.housingType?.value,
    county: updatedData.county?.name || updatedData.county,
    state: updatedData.state?.value || updatedData.state,
    endOfResidence: dayjs(updatedData.endOfResidence).format(COMMON_REQUEST_DATE_FORMAT),
    startOfResidence: dayjs(updatedData.startOfResidence).format(COMMON_REQUEST_DATE_FORMAT),
    monthlyRentAmount: updatedData.monthlyRentAmount
      ? Number(updatedData.monthlyRentAmount)
      : undefined,
    monthlyRelativesPartRentAmount: updatedData.monthlyRelativesPartRentAmount
      ? Number(updatedData.monthlyRelativesPartRentAmount)
      : undefined,
    ...getConditionalObject(updatedData.isOutsideUsa, {
      country: updatedData.country.value,
    }),
  };
  const omittedData = _.omit(formattedUpdatedData, [
    'isNewlyAdded',
    'latitude',
    'longitude',
    'entityId',
  ]);
  omittedData.fullAddress = getFormattedFullAddress({
    address: omittedData,
  });

  return omittedData;
};

export const prepareInitialDeletedFlags = ({
  previousAddresses = [],
  previousAddressesPendingChangesData,
  isBorrowerNewAdded,
  isBorrowerDeleted,
}) =>
  previousAddresses.map(addressValues => {
    const pendingChanges = previousAddressesPendingChangesData[addressValues.id];

    if (!pendingChanges) {
      return { ...addressValues, isAdded: isBorrowerNewAdded, isDeleted: isBorrowerDeleted };
    }

    const historyActionType = pendingChanges.addressId?.actionType;
    const isDeleted = historyActionType === HistoryPendingChangesActionType.DELETED;
    const isAdded = historyActionType === HistoryPendingChangesActionType.ADDED;

    if (!isDeleted) {
      return addressValues;
    }

    return {
      ...addressValues,
      isDeleted,
      isAdded,
    };
  });

export const filterPreviousAddressForm = (pendingChanges, filterFlags = false) => {
  return Object.entries(pendingChanges).reduce((acc, [pendingChangeKey, pendingChangeValue]) => {
    const isHousingTypeChanged = pendingChanges.housingType !== undefined;
    const isHasRelativesPartRent = pendingChanges.hasRelativesPartRent !== undefined;

    const housingTypeNewValue = pendingChanges.housingType?.newValue;
    const hasRelativesPartRentNewValue = pendingChanges.hasRelativesPartRent?.newValue;

    const handlePendingChangeKey = () => {
      switch (pendingChangeKey) {
        case 'isSeparateChange':
          if (!filterFlags) {
            acc[pendingChangeKey] = pendingChangeValue;
          }

          break;

        case 'startOfResidence':
        case 'endOfResidence':
          acc[pendingChangeKey] = formatDatePendingChange(pendingChangeValue);
          break;

        case 'monthlyRentAmount':
          if (!isHousingTypeChanged || housingTypeNewValue === HousingType.RENT) {
            acc[pendingChangeKey] = {
              newValue: pendingChangeValue?.newValue ?? null,
              initialValue: pendingChangeValue?.initialValue ?? null,
            };
          }

          break;

        case 'hasRelativesPartRent':
          {
            const formattedPendingChangeValue = {
              newValue: pendingChangeValue?.newValue ?? false,
              initialValue: pendingChangeValue?.initialValue ?? false,
            };

            if (
              (!isHousingTypeChanged || housingTypeNewValue === HousingType.LIVING_WITH_RELATIVE) &&
              checkBooleanPendingChangeVisibility(formattedPendingChangeValue)
            ) {
              acc[pendingChangeKey] = formattedPendingChangeValue;
            }
          }

          break;

        case 'monthlyRelativesPartRentAmount':
          if (
            (!isHousingTypeChanged || housingTypeNewValue === HousingType.LIVING_WITH_RELATIVE) &&
            (!isHasRelativesPartRent || !!hasRelativesPartRentNewValue)
          ) {
            acc[pendingChangeKey] = {
              newValue: pendingChangeValue?.newValue ?? null,
              initialValue: pendingChangeValue?.initialValue ?? null,
            };
          }

          break;

        case AddressField.LATITUDE:
        case AddressField.LONGITUDE:
          break;

        default:
          acc[pendingChangeKey] = pendingChangeValue;
      }
    };

    handlePendingChangeKey();

    return acc;
  }, {});
};
